import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";

/**
 * OnboardingTour — lightweight one-time guided tour.
 * - Targets each step via `data-testid` or any selector
 * - Shows a spotlight (SVG mask) + a floating card with title + description
 * - Persists completion in localStorage under `ronil.tour.<key>.done`
 * - Dismissible via "Saltar" or finished with "Terminar"
 *
 * Steps shape:
 *   [{ target: "[data-testid=sidebar]", title, body, placement: "right|bottom|left|top" }]
 */
const STORAGE_PREFIX = "ronil.tour.";

export default function OnboardingTour({ tourKey, steps, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const key = `${STORAGE_PREFIX}${tourKey}.done`;

  useEffect(() => {
    try {
      if (localStorage.getItem(key) === "1") return;
      // Wait until the page is mounted so target elements exist
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    } catch { /* localStorage may be unavailable */ }
  }, [key]);

  const current = steps[idx];

  const measure = useCallback(() => {
    if (!current) return;
    const el = document.querySelector(current.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {}
  }, [current]);

  useLayoutEffect(() => {
    if (!visible) return;
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [visible, measure]);

  const dismiss = useCallback((finished) => {
    try { localStorage.setItem(key, "1"); } catch {}
    setVisible(false);
    if (finished && onFinish) onFinish();
  }, [key, onFinish]);

  const next = () => {
    if (idx + 1 < steps.length) setIdx(idx + 1);
    else dismiss(true);
  };

  if (!visible || !current) return null;

  // Dynamic positioning of the hint card
  const placement = current.placement || "right";
  const CARD_W = 320;
  const CARD_PAD = 14;
  let cardStyle = { position: "fixed", width: CARD_W, zIndex: 10001 };
  if (rect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    switch (placement) {
      case "bottom":
        cardStyle.top = Math.min(vh - 200, rect.top + rect.height + CARD_PAD);
        cardStyle.left = Math.min(vw - CARD_W - 12, Math.max(12, rect.left));
        break;
      case "left":
        cardStyle.top = Math.max(12, rect.top);
        cardStyle.left = Math.max(12, rect.left - CARD_W - CARD_PAD);
        break;
      case "top":
        cardStyle.top = Math.max(12, rect.top - 180);
        cardStyle.left = Math.min(vw - CARD_W - 12, Math.max(12, rect.left));
        break;
      case "right":
      default:
        cardStyle.top = Math.max(12, rect.top);
        cardStyle.left = Math.min(vw - CARD_W - 12, rect.left + rect.width + CARD_PAD);
        break;
    }
    // Fallback: if rect is near bottom and placement would overflow, flip to top
    if (cardStyle.top + 180 > vh) cardStyle.top = Math.max(12, vh - 200);
  } else {
    // Center fallback when target is missing
    cardStyle.top = window.innerHeight / 2 - 120;
    cardStyle.left = window.innerWidth / 2 - CARD_W / 2;
  }

  return createPortal(
    <>
      {/* Spotlight overlay (SVG mask) */}
      <svg
        className="fixed inset-0 pointer-events-auto"
        style={{ zIndex: 10000 }}
        width="100%" height="100%"
        onClick={() => next()}
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(12,26,51,0.72)" mask="url(#tour-mask)" />
        {rect && (
          <rect
            x={rect.left - 6}
            y={rect.top - 6}
            width={rect.width + 12}
            height={rect.height + 12}
            rx="8"
            fill="none"
            stroke="#C9A96E"
            strokeWidth="2.5"
            style={{ filter: "drop-shadow(0 0 10px rgba(201,169,110,0.9))" }}
          />
        )}
      </svg>

      {/* Hint card */}
      <div
        style={cardStyle}
        className="bg-white border border-ronil-navy rounded-lg shadow-elevation overflow-hidden pointer-events-auto"
        data-testid={`tour-step-${idx}`}
      >
        <div className="bg-ronil-navy text-white px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-heading font-semibold">
            <Sparkles className="w-3.5 h-3.5 text-ronil-sand" />
            <span>Passo {idx + 1} de {steps.length}</span>
          </div>
          <button
            onClick={() => dismiss(false)}
            data-testid="tour-skip"
            className="text-white/80 hover:text-white p-1 -mr-1"
            aria-label="Saltar"
            title="Saltar tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-display text-lg font-bold text-ronil-navy mb-1 leading-tight">{current.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{current.body}</p>

          <div className="mt-5 flex items-center justify-between gap-2">
            <button
              onClick={() => dismiss(false)}
              className="text-xs text-slate-500 hover:text-slate-800 hover:underline font-medium"
              data-testid="tour-dismiss"
            >
              Não mostrar novamente
            </button>
            <button
              onClick={next}
              data-testid="tour-next"
              className="inline-flex items-center gap-1.5 bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md px-3.5 py-1.5 text-sm font-heading font-semibold transition-colors"
            >
              {idx + 1 === steps.length ? "Terminar" : "Seguinte"}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Clear the tour completion flag so it shows again. Useful for testing. */
export function resetTour(tourKey) {
  try { localStorage.removeItem(`${STORAGE_PREFIX}${tourKey}.done`); } catch {}
}
